import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  ApiDataAccess,
  base64Encode,
  blobToDataURL,
  ConnectionService,
  createHttpResourceLocation,
  ResponseRequestEvent,
} from '@softline/core';
import { lastValueFrom, toArray } from 'rxjs';
import {
  SOFTLINE_API_GARAGE_FILE,
  SOFTLINE_API_GARAGE_ORDER,
  SOFTLINE_API_GARAGE_ORDER_CLOSE,
  SOFTLINE_API_GARAGE_ORDER_SIGN,
  SOFTLINE_API_GARAGE_ORDERS,
  SOFTLINE_API_GARAGE_ORDERS_DEFAULT,
  SOFTLINE_API_GARAGE_UNFINISHED_ORDERS,
} from '../garage.api';
import { File, GarageOrder } from '../models/garage-order';
import { OrderPage } from '../models/order-page';

export type GarageOrderCloseMethod = 'approval' | 'deliveryNote' | 'invoice';

@Injectable()
export class GarageOrderRepository {
  readonly #dataAccess = inject(ApiDataAccess);
  readonly #http = inject(HttpClient);
  readonly #connectionService = inject(ConnectionService);

  retrieveById(orderId: number): Promise<GarageOrder> {
    return this.#dataAccess.get({
      path: SOFTLINE_API_GARAGE_ORDER,
      pathParams: { id: orderId },
    });
  }

  retrieve(
    query: string | undefined,
    limit: number | undefined,
    offset?: number | undefined,
    onlyOwn?: boolean | undefined
  ): Promise<OrderPage> {
    return this.#dataAccess.get({
      path: SOFTLINE_API_GARAGE_ORDERS,
      queryParams: { limit, offset, query, onlyOwn: onlyOwn || undefined },
    });
  }

  create(order: GarageOrder): Promise<GarageOrder> {
    return this.#dataAccess.create(
      {
        path: SOFTLINE_API_GARAGE_ORDERS,
      },
      order
    );
  }

  update(orderId: number, order: GarageOrder): Promise<GarageOrder> {
    return this.#dataAccess.update(
      {
        path: SOFTLINE_API_GARAGE_ORDER,
        pathParams: { id: orderId },
      },
      order
    );
  }

  close(
    orderId: number,
    method: GarageOrderCloseMethod = 'approval'
  ): Promise<GarageOrder> {
    return this.#dataAccess.create(
      {
        path: SOFTLINE_API_GARAGE_ORDER_CLOSE,
        pathParams: { id: orderId },
      },
      { type: method }
    );
  }

  //TODO: Implement delete method in backend
  /* delete(id: number): Promise<void> {
    return this.#dataAccess.delete(
      this.createLocation(SOFTLINE_API_GARAGE_ORDERS, { id })
    );
  }*/

  retrieveByMechanicId(orderId: number): Promise<GarageOrder[]> {
    return this.#dataAccess.get({
      path: SOFTLINE_API_GARAGE_UNFINISHED_ORDERS,
      pathParams: { id: orderId },
    });
  }

  retrieveDefault(): Promise<GarageOrder> {
    return this.#dataAccess.create(
      {
        path: SOFTLINE_API_GARAGE_ORDERS_DEFAULT,
      },
      {}
    );
  }

  async signOrder(orderId: number, signature: Blob): Promise<GarageOrder> {
    const dataUrl = await blobToDataURL(signature);
    const base64 = dataUrl.replace(/^data:[^;]*;base64,/, '');
    const connection = this.#connectionService.connection();
    const { url } = createHttpResourceLocation({
      host: connection.host,
      port: connection.port,
      path: connection.basePath
        ? [connection.basePath, SOFTLINE_API_GARAGE_ORDER_SIGN]
        : [SOFTLINE_API_GARAGE_ORDER_SIGN],
      pathParams: { id: orderId },
    });
    const headers = new HttpHeaders().set('Content-Type', 'text/plain');
    return lastValueFrom(
      this.#http.post<GarageOrder>(url, base64, { headers })
    );
  }

  async getSignatureImage(reference: File): Promise<Blob> {
    const encoded = base64Encode(JSON.stringify(reference));
    const events = await lastValueFrom(
      this.#dataAccess
        .download({ path: `${SOFTLINE_API_GARAGE_FILE}/${encoded}` })
        .pipe(toArray())
    );
    const response = events.find(
      (e): e is ResponseRequestEvent<Blob> => !!e && e.type === 'response'
    );
    return (response?.response as Blob) ?? new Blob();
  }
}
